// Strona szczegółów oferty SalesCRM dla klienta.
// Klient widzi pozycje read-only, komentarze (lista + dodawanie),
// w statusie TO_ACCEPT może zaakceptować lub odrzucić ofertę.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, CircularProgress, Tooltip } from '@mui/material';
import { useQueryClient } from 'react-query';
import EventIcon from '@mui/icons-material/Event';
import PaymentsIcon from '@mui/icons-material/Payments';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';

import { reduxActions, useDispatch } from 'store';
import {
  getSalesCrmMyOfferQueryKey,
  getSalesCrmMyOffersQueryKey,
  useGetSalesCrmMyOffer,
  usePostSalesCrmMyOfferFulfill,
  usePutSalesCrmMyOfferAccept,
  usePutSalesCrmMyOfferReject
} from 'api';
import { OfferStatusBadge, OFFER_STATUS } from 'components/controls';
import ClientOfferProductsContainer from 'components/containers/ClientOfferProductsContainer';
import ClientOfferCommentsCard from 'components/containers/ClientOfferCommentsCard';

import RejectOfferModal from './RejectOfferModal';

const formatDate = (raw: string | null): string => {
  if (!raw) return '-';
  const isoDate = raw.length >= 10 ? raw.substring(0, 10) : raw;
  const match = isoDate.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (match) return `${match[3]}.${match[2]}.${match[1]}`;
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return raw;
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()}`;
};

const formatPrice = (raw: number | null | undefined): string => {
  if (raw == null || Number.isNaN(raw)) return '-';
  return `${raw.toFixed(2).replace('.', ',')} zł`;
};

const ClientOfferDetails: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { offerId = '' } = useParams<{ offerId: string }>();

  const [isRejectOpen, setIsRejectOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [selectedVariants, setSelectedVariants] = useState<Record<number, number>>({});
  const [variantPositionIds, setVariantPositionIds] = useState<number[]>([]);

  const handleVariantPositionsResolved = useCallback((ids: number[]) => {
    setVariantPositionIds(ids);
  }, []);

  const { data: offer, isLoading, isError } = useGetSalesCrmMyOffer(offerId);

  useEffect(() => {
    dispatch(
      reduxActions.setBreadcrumbs([
        { name: t('Dashboard'), path: '/dashboard' },
        { name: t('Oferty dedykowane'), path: '/dashboard/offers/dedicated' },
        { name: offer?.number ?? `#${offerId}` }
      ])
    );
  }, [offer?.number, offerId]);

  const invalidateOffer = () => {
    queryClient.invalidateQueries(getSalesCrmMyOfferQueryKey(offerId));
    queryClient.invalidateQueries(getSalesCrmMyOffersQueryKey());
    // Po akceptacji oferty powstaje nowy koszyk - invalidujemy cache 'carts-all'
    // i 'carts', zeby Cart po przejsciu na /cart/{id} mial swieze cartsData i
    // nie pokazywal "Koszyk jest pusty" (currentCart.products_count = 0 z cache).
    queryClient.invalidateQueries('carts-all');
    queryClient.invalidateQueries(['carts']);
  };

  const { mutate: acceptOffer, isLoading: isAccepting } = usePutSalesCrmMyOfferAccept(offerId, {
    onSuccess: () => {
      // Po akceptacji zostajemy na stronie i refetchujemy ofertę -
      // backend tworzy koszyk na podstawie oferty i zwraca jego ID w polu `cartId`.
      // Po refetchu pokaże się przycisk "Złóż zamówienie na podstawie oferty".
      invalidateOffer();
    },
    onError: (e) => setErrorMessage(e?.message ?? t('Nie udało się zaakceptować oferty.'))
  });

  const { mutate: rejectOffer, isLoading: isRejecting } = usePutSalesCrmMyOfferReject(offerId, {
    onSuccess: () => {
      setIsRejectOpen(false);
      invalidateOffer();
      navigate('/dashboard/offers/dedicated');
    },
    onError: (e) => setErrorMessage(e?.message ?? t('Nie udało się odrzucić oferty.'))
  });

  const { mutate: fulfillOffer, isLoading: isFulfilling } = usePostSalesCrmMyOfferFulfill(offerId, {
    onSuccess: (response) => {
      invalidateOffer();
      const cartId = response?.data?.cart_id;
      const orderId = response?.data?.order_id;
      if (orderId) {
        navigate(`/dashboard/orders/${orderId}`);
      } else if (cartId) {
        navigate(`/cart/${cartId}`);
      }
    },
    onError: (e) => setErrorMessage(e?.message ?? t('Nie udało się zrealizować oferty ponownie.'))
  });

  const canDecide = offer?.status === OFFER_STATUS.TO_ACCEPT;

  const missingVariantCount = useMemo(
    () => variantPositionIds.filter((id) => selectedVariants[id] == null).length,
    [variantPositionIds, selectedVariants]
  );
  const canSubmitAccept = missingVariantCount === 0;

  const handleAcceptClick = () => {
    if (!canSubmitAccept) return;
    const payload =
      variantPositionIds.length > 0
        ? {
            selectedVariants: variantPositionIds.map((positionId) => ({
              positionId,
              qtyFrom: selectedVariants[positionId]
            }))
          }
        : undefined;
    acceptOffer(payload);
  };

  // Oferta okresowa moze byc realizowana ponownie az do konca okresu waznosci.
  // Pelna walidacja (status=ACCEPTED, isPeriodic, validUntil, MAIN_USER) jest po stronie
  // backendu w OfferFulfillmentValidator - tu sprawdzamy tylko widocznosc przycisku.
  //
  // validUntil = null oznacza oferte bezterminowa (zawsze wazna), nie wygasla -
  // zgodnie z semantyka OfferFulfillmentValidator.
  const isValidUntilOk = (() => {
    if (!offer?.validUntil) return true; // bezterminowa
    const validUntilDate = new Date(offer.validUntil);
    if (Number.isNaN(validUntilDate.getTime())) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return validUntilDate >= today;
  })();

  const canFulfillAgain =
    offer?.isPeriodic === true &&
    offer?.status === OFFER_STATUS.ACCEPTED &&
    isValidUntilOk;

  if (isLoading) {
    return (
      <div style={{ textAlign: 'center', padding: 60 }}>
        <CircularProgress size={36} sx={{ color: '#6b54ff' }} />
      </div>
    );
  }

  if (isError || !offer) {
    return (
      <div
        style={{
          padding: 24,
          background: '#fde6ea',
          color: '#b3142d',
          borderRadius: 8,
          margin: '16px 0',
          fontWeight: 500
        }}>
        {t('Nie udało się pobrać oferty lub nie masz do niej dostępu.')}
      </div>
    );
  }

  return (
    <div style={{ padding: '16px 0', display: 'flex', flexDirection: 'column', gap: 20 }}>
      {/* HERO HEADER - gradient z numerem oferty i statusem */}
      <div
        style={{
          background: 'linear-gradient(135deg, #6b54ff 0%, #8a76ff 100%)',
          color: '#fff',
          borderRadius: 12,
          padding: '28px 32px',
          boxShadow: '0 4px 16px rgba(107, 84, 255, 0.18)'
        }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            flexWrap: 'wrap',
            gap: 16
          }}>
          <div>
            <div
              style={{
                fontSize: 11,
                letterSpacing: 1.2,
                textTransform: 'uppercase',
                opacity: 0.85,
                marginBottom: 4
              }}>
              {t('Numer oferty')}
            </div>
            <div style={{ fontSize: 28, fontWeight: 700, letterSpacing: 0.5 }}>
              {offer.number ?? `#${offer.id}`}
            </div>
          </div>
          <div style={{ background: '#fff', padding: '4px 8px', borderRadius: 12 }}>
            <OfferStatusBadge status={offer.status} label={offer.statusFormatted} />
          </div>
        </div>
      </div>

      {/* STATS GRID - 3 karty z ikonkami */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
          gap: 16
        }}>
        <StatCard
          icon={<EventIcon style={{ color: '#6b54ff' }} />}
          label={t('Data ważności')}
          value={formatDate(offer.validUntil)}
        />
        <StatCard
          icon={<PaymentsIcon style={{ color: '#1f7f48' }} />}
          label={t('Wartość netto')}
          value={formatPrice(offer.valueNet)}
          highlight
        />
        <StatCard
          icon={<ReceiptLongIcon style={{ color: '#5a47e0' }} />}
          label={t('Wartość brutto')}
          value={formatPrice(offer.valueGross)}
        />
      </div>

      {/* CALL TO ACTION - oferta okresowa, klient moze ponownie zrealizowac w okresie waznosci */}
      {canFulfillAgain && (
        <div
          style={{
            background: 'linear-gradient(90deg, #e8efff 0%, #f4f7ff 100%)',
            border: '1px solid #6b54ff',
            borderRadius: 12,
            padding: 20,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 16
          }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, color: '#3f3299', marginBottom: 4 }}>
              {t('Oferta okresowa - możesz zrealizować ponownie')}
            </div>
            <div style={{ fontSize: 13, color: '#3f3299', opacity: 0.85 }}>
              {t('Do końca okresu ważności możesz wielokrotnie zrealizować tę ofertę.')}
            </div>
          </div>
          <Button
            onClick={() => fulfillOffer()}
            variant="contained"
            disabled={isFulfilling}
            startIcon={<ShoppingCartIcon />}
            sx={{
              backgroundColor: '#6b54ff',
              fontWeight: 600,
              '&:hover': { backgroundColor: '#5a47e0' }
            }}>
            {isFulfilling ? <CircularProgress size={18} /> : t('Zrealizuj ponownie')}
          </Button>
        </div>
      )}

      {/* CALL TO ACTION - gdy oferta zaakceptowana i backend utworzyl koszyk */}
      {offer.cartId !== null && (
        <div
          style={{
            background: 'linear-gradient(90deg, #e2f4ea 0%, #f0faf4 100%)',
            border: '1px solid #74c190',
            borderRadius: 12,
            padding: 20,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 16
          }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, color: '#155a30', marginBottom: 4 }}>
              {t('Koszyk został utworzony na podstawie oferty')}
            </div>
            <div style={{ fontSize: 13, color: '#155a30', opacity: 0.85 }}>
              {t('Możesz teraz przejść do koszyka i złożyć zamówienie na podstawie zaakceptowanej oferty.')}
            </div>
          </div>
          <Button
            onClick={() => navigate(`/cart/${offer.cartId}`)}
            variant="contained"
            startIcon={<ShoppingCartIcon />}
            sx={{
              backgroundColor: '#1f7f48',
              fontWeight: 600,
              '&:hover': { backgroundColor: '#1a6939' }
            }}>
            {t('Złóż zamówienie na podstawie oferty')}
          </Button>
        </div>
      )}

      {/* CALL TO ACTION - gdy TO_ACCEPT */}
      {canDecide && (
        <div
          style={{
            background: 'linear-gradient(90deg, #fdf4e7 0%, #fff8ec 100%)',
            border: '1px solid #f7c873',
            borderRadius: 12,
            padding: 20,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 16
          }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, color: '#7a5810', marginBottom: 4 }}>
              {t('Oferta czeka na Twoją decyzję')}
            </div>
            <div style={{ fontSize: 13, color: '#7a5810', opacity: 0.85 }}>
              {t('Po zapoznaniu się z pozycjami zaakceptuj lub odrzuć ofertę.')}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 12 }}>
            <Button
              onClick={() => setIsRejectOpen(true)}
              variant="outlined"
              disabled={isAccepting || isRejecting}
              startIcon={<CancelIcon />}
              sx={{
                borderColor: '#e02e4e',
                color: '#e02e4e',
                fontWeight: 600,
                '&:hover': { borderColor: '#c01a3a', backgroundColor: 'rgba(224,46,78,0.04)' }
              }}>
              {t('Odrzuć ofertę')}
            </Button>
            <Tooltip
              title={
                !canSubmitAccept
                  ? t('Wybierz wariant dla każdej pozycji z opcjami do wyboru ({{count}} pozostało).', {
                      count: missingVariantCount
                    })
                  : ''
              }
              arrow
              disableHoverListener={canSubmitAccept}>
              <span>
                <Button
                  onClick={handleAcceptClick}
                  variant="contained"
                  disabled={isAccepting || isRejecting || !canSubmitAccept}
                  startIcon={<CheckCircleIcon />}
                  sx={{
                    backgroundColor: '#1f7f48',
                    fontWeight: 600,
                    '&:hover': { backgroundColor: '#1a6939' }
                  }}>
                  {isAccepting ? <CircularProgress size={18} /> : t('Akceptuj ofertę')}
                </Button>
              </span>
            </Tooltip>
          </div>
        </div>
      )}

      {/* SEKCJA POZYCJI */}
      <SectionCard
        icon={<ShoppingCartIcon style={{ color: '#6b54ff' }} />}
        title={t('Pozycje oferty')}>
        <ClientOfferProductsContainer
          offerId={offerId}
          selectedVariants={selectedVariants}
          onVariantsChange={setSelectedVariants}
          onVariantPositionsResolved={handleVariantPositionsResolved}
          readOnly={!canDecide}
        />
      </SectionCard>

      {/* SEKCJA KOMENTARZY */}
      <ClientOfferCommentsCard offerId={offerId} />

      {errorMessage && (
        <div
          style={{
            color: '#b3142d',
            padding: 12,
            background: '#fde6ea',
            borderRadius: 8,
            fontWeight: 500
          }}>
          {errorMessage}
        </div>
      )}

      <RejectOfferModal
        open={isRejectOpen}
        isSubmitting={isRejecting}
        onClose={() => setIsRejectOpen(false)}
        onSubmit={(reason) => rejectOffer({ reason: reason || null })}
      />
    </div>
  );
};

// === Pomocnicze komponenty ===

const StatCard: React.FC<{
  icon: React.ReactNode;
  label: string;
  value: string;
  highlight?: boolean;
}> = ({ icon, label, value, highlight = false }) => (
  <div
    style={{
      background: '#fff',
      border: `1px solid ${highlight ? '#cfe8d7' : '#eef0f4'}`,
      borderRadius: 12,
      padding: 18,
      display: 'flex',
      alignItems: 'center',
      gap: 14,
      boxShadow: '0 2px 6px rgba(0,0,0,0.03)'
    }}>
    <div
      style={{
        width: 44,
        height: 44,
        borderRadius: 10,
        background: highlight ? '#e2f4ea' : '#f1eeff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
      {icon}
    </div>
    <div>
      <div
        style={{
          fontSize: 11,
          color: '#4b556a',
          textTransform: 'uppercase',
          letterSpacing: 0.5,
          marginBottom: 2
        }}>
        {label}
      </div>
      <div style={{ fontSize: 18, fontWeight: 700, color: '#1a1d29' }}>{value}</div>
    </div>
  </div>
);

const SectionCard: React.FC<{
  icon?: React.ReactNode;
  title: string;
  children: React.ReactNode;
}> = ({ icon, title, children }) => (
  <div
    style={{
      background: '#fff',
      border: '1px solid #eef0f4',
      borderRadius: 12,
      overflow: 'hidden',
      boxShadow: '0 2px 6px rgba(0,0,0,0.03)'
    }}>
    <div
      style={{
        padding: '16px 20px',
        borderBottom: '1px solid #eef0f4',
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        background: '#fafbfc'
      }}>
      {icon}
      <div style={{ fontSize: 15, fontWeight: 700, color: '#1a1d29' }}>{title}</div>
    </div>
    <div>{children}</div>
  </div>
);

export default ClientOfferDetails;
